/**
 * Topic Card Service
 *
 * Selects archetype-appropriate data entry cards for the focused data
 * entry phase (Phase 3) of onboarding. Each card is a mini-form with
 * 3–8 settings fields grouped by topic.
 *
 * Card selection is based on the user's IntakeProfile answers (life stage,
 * employment type, housing, investment level, debt situation).
 */

import type { IntakeProfile } from "../types/intake";

/** A single field reference within a topic card. */
export interface TopicCardField {
  /** Settings field name (e.g., "employmentIncome", "cashBalance") */
  fieldName: string;
  /** Human-readable label shown to the user */
  label: string;
  /** Settings category this field belongs to */
  category: string;
}

/** A topic card presented during focused data entry. */
export interface TopicCard {
  /** Unique card identifier */
  id: string;
  /** Card title shown to the user */
  title: string;
  /** Emoji icon for the card */
  icon: string;
  /** Fields to fill within this card */
  fields: TopicCardField[];
}

// ── Card Definitions ─────────────────────────────────────────────

const CARD_INCOME_SAVINGS: TopicCard = {
  id: "income-savings",
  title: "Your Income & Savings",
  icon: "💰",
  fields: [
    {
      fieldName: "employmentIncome",
      label: "Employment income",
      category: "income",
    },
    {
      fieldName: "cashBalance",
      label: "Cash & savings",
      category: "investableAssets",
    },
    {
      fieldName: "tfsaBalance",
      label: "TFSA balance",
      category: "investableAssets",
    },
    {
      fieldName: "rrspBalance",
      label: "RRSP balance",
      category: "investableAssets",
    },
  ],
};

const CARD_MONTHLY_SPENDING: TopicCard = {
  id: "monthly-spending",
  title: "Your Monthly Spending",
  icon: "📊",
  fields: [
    {
      fieldName: "housingCost",
      label: "Rent / housing cost",
      category: "expenses",
    },
    {
      fieldName: "foodGroceries",
      label: "Food & groceries",
      category: "expenses",
    },
    {
      fieldName: "transportation",
      label: "Transportation",
      category: "expenses",
    },
    {
      fieldName: "otherExpenses",
      label: "Other expenses",
      category: "expenses",
    },
  ],
};

const CARD_SAVINGS_INVESTMENTS: TopicCard = {
  id: "savings-investments",
  title: "Your Savings & Investments",
  icon: "📈",
  fields: [
    {
      fieldName: "rrspBalance",
      label: "RRSP balance",
      category: "investableAssets",
    },
    {
      fieldName: "tfsaBalance",
      label: "TFSA balance",
      category: "investableAssets",
    },
    {
      fieldName: "pensionValue",
      label: "Pension value",
      category: "investableAssets",
    },
    {
      fieldName: "cashBalance",
      label: "Cash & savings",
      category: "investableAssets",
    },
    {
      fieldName: "nonRegInvestments",
      label: "Non-registered investments",
      category: "investableAssets",
    },
  ],
};

const CARD_HOME_DEBTS: TopicCard = {
  id: "home-debts",
  title: "Your Home & Debts",
  icon: "🏠",
  fields: [
    {
      fieldName: "primaryResidenceValue",
      label: "Home value",
      category: "realEstateAssets",
    },
    {
      fieldName: "mortgageBalance",
      label: "Mortgage balance",
      category: "liabilities",
    },
    {
      fieldName: "mortgageRate",
      label: "Mortgage rate",
      category: "liabilities",
    },
    {
      fieldName: "mortgagePayment",
      label: "Monthly payment",
      category: "liabilities",
    },
  ],
};

const CARD_INCOME_SPENDING: TopicCard = {
  id: "income-spending",
  title: "Income & Spending",
  icon: "💼",
  fields: [
    {
      fieldName: "employmentIncome",
      label: "Employment income",
      category: "income",
    },
    { fieldName: "housingCost", label: "Housing costs", category: "expenses" },
    {
      fieldName: "otherExpenses",
      label: "Other expenses",
      category: "expenses",
    },
  ],
};

const CARD_RETIREMENT_INCOME: TopicCard = {
  id: "retirement-income",
  title: "Retirement Income Plan",
  icon: "🎯",
  fields: [
    {
      fieldName: "pensionValue",
      label: "Pension value",
      category: "investableAssets",
    },
    { fieldName: "cppStartAge", label: "CPP start age", category: "income" },
    { fieldName: "oasStartAge", label: "OAS start age", category: "income" },
    {
      fieldName: "nonRegInvestments",
      label: "Non-registered investments",
      category: "investableAssets",
    },
  ],
};

const CARD_NEST_EGG: TopicCard = {
  id: "nest-egg",
  title: "Your Nest Egg",
  icon: "🥚",
  fields: [
    {
      fieldName: "rrspBalance",
      label: "RRSP balance",
      category: "investableAssets",
    },
    {
      fieldName: "tfsaBalance",
      label: "TFSA balance",
      category: "investableAssets",
    },
    {
      fieldName: "rrifBalance",
      label: "RRIF balance",
      category: "investableAssets",
    },
    {
      fieldName: "nonRegInvestments",
      label: "Non-registered investments",
      category: "investableAssets",
    },
    {
      fieldName: "cashBalance",
      label: "Cash & savings",
      category: "investableAssets",
    },
  ],
};

const CARD_YOUR_SPENDING: TopicCard = {
  id: "your-spending",
  title: "Your Spending",
  icon: "💳",
  fields: [
    { fieldName: "housingCost", label: "Housing costs", category: "expenses" },
    {
      fieldName: "foodGroceries",
      label: "Food & groceries",
      category: "expenses",
    },
    {
      fieldName: "otherExpenses",
      label: "Other expenses",
      category: "expenses",
    },
  ],
};

const CARD_INCOME_STREAMS: TopicCard = {
  id: "income-streams",
  title: "Your Income Streams",
  icon: "🌊",
  fields: [
    { fieldName: "pensionIncome", label: "Pension income", category: "income" },
    { fieldName: "cppIncome", label: "CPP income", category: "income" },
    { fieldName: "oasIncome", label: "OAS income", category: "income" },
    { fieldName: "otherIncome", label: "Other income", category: "income" },
  ],
};

const CARD_RETIREE_SAVINGS: TopicCard = {
  id: "retiree-savings",
  title: "Your Savings",
  icon: "🏦",
  fields: [
    {
      fieldName: "rrifBalance",
      label: "RRIF balance",
      category: "investableAssets",
    },
    {
      fieldName: "tfsaBalance",
      label: "TFSA balance",
      category: "investableAssets",
    },
    {
      fieldName: "nonRegInvestments",
      label: "Non-registered investments",
      category: "investableAssets",
    },
    {
      fieldName: "cashBalance",
      label: "Cash & savings",
      category: "investableAssets",
    },
  ],
};

const CARD_MONTHLY_EXPENSES: TopicCard = {
  id: "monthly-expenses",
  title: "Monthly Expenses",
  icon: "📋",
  fields: [
    { fieldName: "housingCost", label: "Housing costs", category: "expenses" },
    {
      fieldName: "foodGroceries",
      label: "Food & groceries",
      category: "expenses",
    },
    {
      fieldName: "transportation",
      label: "Transportation",
      category: "expenses",
    },
    {
      fieldName: "otherExpenses",
      label: "Other expenses",
      category: "expenses",
    },
  ],
};

const CARD_BUSINESS_INCOME: TopicCard = {
  id: "business-income",
  title: "Your Business & Income",
  icon: "💻",
  fields: [
    {
      fieldName: "selfEmploymentIncome",
      label: "Business income",
      category: "income",
    },
    {
      fieldName: "employmentIncome",
      label: "Other employment income",
      category: "income",
    },
  ],
};

const CARD_DEBTS_OBLIGATIONS: TopicCard = {
  id: "debts-obligations",
  title: "Debts & Obligations",
  icon: "📝",
  fields: [
    {
      fieldName: "mortgageBalance",
      label: "Mortgage balance",
      category: "liabilities",
    },
    {
      fieldName: "creditCardBalance",
      label: "Credit card debt",
      category: "liabilities",
    },
    {
      fieldName: "otherLiabilities",
      label: "Other debt",
      category: "liabilities",
    },
  ],
};

// ── Selection Logic ──────────────────────────────────────────────

/**
 * Select the appropriate topic cards based on the user's IntakeProfile.
 * Returns 2–4 cards ordered by estimated impact on projections.
 */
export function getTopicCardsForProfile(profile: IntakeProfile): TopicCard[] {
  // Business owner archetype takes priority over life stage
  if (profile.employment === "self-employed") {
    return [
      CARD_BUSINESS_INCOME,
      CARD_SAVINGS_INVESTMENTS,
      CARD_DEBTS_OBLIGATIONS,
      CARD_MONTHLY_SPENDING,
    ];
  }

  // Retiree archetype
  if (profile.lifeStage === "retired") {
    return [CARD_INCOME_STREAMS, CARD_RETIREE_SAVINGS, CARD_MONTHLY_EXPENSES];
  }

  // Pre-retiree archetype
  if (profile.lifeStage === "approaching") {
    const cards: TopicCard[] = [
      CARD_RETIREMENT_INCOME,
      CARD_NEST_EGG,
      CARD_YOUR_SPENDING,
    ];
    if (
      profile.housing === "own-mortgage" ||
      profile.housing === "own-investment"
    ) {
      cards.push(CARD_HOME_DEBTS);
    }
    return cards;
  }

  // Mid-career homeowner archetype
  if (
    (profile.lifeStage === "building" ||
      profile.lifeStage === "peak-earning") &&
    (profile.housing === "own-mortgage" ||
      profile.housing === "own-free" ||
      profile.housing === "own-investment") &&
    profile.investmentLevel !== "beginner"
  ) {
    return [CARD_SAVINGS_INVESTMENTS, CARD_HOME_DEBTS, CARD_INCOME_SPENDING];
  }

  // Default: Early Career Renter
  return [CARD_INCOME_SAVINGS, CARD_MONTHLY_SPENDING];
}
